import { gql } from '@apollo/client';
import IconComment from '@apollo/icons/default/IconComment.svg';
import IconRocket from '@apollo/icons/default/IconRocket.svg';
import IconSuccess from '@apollo/icons/default/IconSuccess.svg';
import IconSync from '@apollo/icons/default/IconSync.svg';
import IconTime from '@apollo/icons/default/IconTime.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import IconServices from '@apollo/icons/small/IconServices.svg';
import {
  Avatar,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardHeaderControl,
  Code,
  Group,
  Link,
  Text,
} from '@apollo/orbit';
import { Moon } from '@apollo/space-kit/illustrations/Moon';
import classnames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import {
  launchesRouteConfig,
  proposalChangesRouteConfig,
  proposalEditorRouteConfig,
  proposalHomepageRouteConfig,
  variantHomepageRouteConfig,
} from 'src/app/graph/routes';
import {
  LocalTimestamp,
  RelativeTimestamp,
} from 'src/components/timestamp/Timestamp';
import {
  assertUnreachable,
  assertUnreachableOrReturnDefault,
} from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  ProposalActivityAction,
  ReviewDecision,
} from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import * as routes from '../../../routes';
import { SafeCommentMarkdown } from '../../CommentMarkdownEditor/SafeCommentMarkdown';
import { useGetUsersForMentions } from '../../CommentMarkdownEditor/useMentionUsers';

import {
  ActivityFeedCommentThread,
  ActivityFeedCommentThreadChangeProposalCommentFragment,
  ActivityFeedCommentThreadGeneralProposalCommentFragment,
} from './ActivityFeedCommentThread';
import {
  ActivityFeedRevision,
  ActivityFeedRevisionFragment,
} from './ActivityFeedRevision';

export const ActivityFeedProposalFragment = gql`
  fragment ActivityFeedProposalFragment on Proposal {
    id
    activities(after: $after, first: $first) {
      totalCount
      pageInfo {
        endCursor
        startCursor
        hasPreviousPage
        hasNextPage
      }
      edges {
        cursor
        node {
          id
          activity
          createdAt
          createdBy {
            id
            ... on User {
              fullName
              avatarUrl
            }
          }
          target {
            ... on ProposalReview {
              id
              comment {
                id
                message
              }
              decision
              createdBy {
                id
                name
                ... on User {
                  fullName
                  avatarUrl
                }
              }
            }
            ... on ProposalRevision {
              id
              ...ActivityFeedRevisionFragment
            }
            ... on ParentChangeProposalComment {
              id
              ...ActivityFeedCommentThreadChangeProposalCommentFragment
            }
            ... on ParentGeneralProposalComment {
              id
              ...ActivityFeedCommentThreadGeneralProposalCommentFragment
            }
            ... on ProposalFullImplementationProposalOrigin {
              id
              variant {
                id
                name
              }
              revision @include(if: $canQueryLaunches) {
                id
              }
            }
            ... on ProposalFullImplementationVariantOrigin {
              id
              variant {
                id
                name
              }
              launch @include(if: $canQueryLaunches) {
                id
              }
            }
            ... on ProposalPartialImplementationProposalOrigin {
              id
              variant {
                id
                name
              }
              revision @include(if: $canQueryLaunches) {
                id
              }
            }
            ... on ProposalPartialImplementationVariantOrigin {
              id
              variant {
                id
                name
              }
              launch @include(if: $canQueryLaunches) {
                id
              }
            }
          }
        }
      }
    }
  }
  ${ActivityFeedCommentThreadChangeProposalCommentFragment}
  ${ActivityFeedCommentThreadGeneralProposalCommentFragment}

  ${ActivityFeedRevisionFragment}
`;

export const ActivityFeedFirstLaunchGraphVariantFragment = gql`
  fragment ActivityFeedFirstLaunchGraphVariantFragment on GraphVariant {
    id
    launchHistory(limit: 1, orderBy: CREATED_ASC)
      @include(if: $canQueryLaunches) {
      id
      createdAt
      buildInput {
        ... on CompositionBuildInput {
          subgraphs {
            hash
            name
          }
        }
      }
    }
  }
`;

export const ActivityFeed = ({
  graphRef,
  proposal,
  variant,
  usersForMentions,
  canQueryLaunches,
  sourceGraphVariant,
}: {
  graphRef: GraphRef;
  proposal: GraphQLTypes.ActivityFeedProposalFragment &
    GraphQLTypes.UseAddCommentProposalFragment;
  variant: GraphQLTypes.ActivityFeedFirstLaunchGraphVariantFragment;
  usersForMentions: ReturnType<typeof useGetUsersForMentions>;
  canQueryLaunches: boolean;
  sourceGraphVariant: string | undefined;
}) => {
  const location = useLocation();

  if (!isMinLengthArray(1, proposal.activities.edges)) {
    return (
      <div className="m-auto w-120 text-center">
        <Moon className="mb-4" />
        <div className="mb-4 text-2xl font-semibold">
          No proposal activity yet
        </div>
        <div className="mb-5">
          Get started by creating and publishing schema changes!
        </div>
        <Button
          as={ReactRouterLink}
          to={proposalEditorRouteConfig.locationFrom({
            location,
            fromRouteConfig: proposalHomepageRouteConfig,
          })}
        >
          Go to Schema Editor
        </Button>
      </div>
    );
  }

  const firstLaunchHistoryItem = variant.launchHistory?.[0];

  return (
    <div
      className={classnames('relative', {
        "before:absolute before:bottom-0 before:left-[19px] before:top-10 before:w-px before:bg-neutral before:content-['']":
          proposal.activities.edges?.length > 1,
      })}
    >
      {proposal.activities.edges
        .filter(
          (
            edge,
          ): edge is typeof edge & {
            node: NonNullable<(typeof edge)['node']>;
          } => !!edge.node,
        )
        .map(({ node: activityNode }, index, activities) => {
          // We record an activity item for each reviewer that is automatically re requested
          // on a proposal, but right now we just show a single card that reviewers generally
          // have been re requested, so filter out multiple requested reviewers in a row for
          // activity type APPROVAL_WITHDRAWN_ON_PUBLISH
          const previousActivity = index > 0 ? activities[index - 1] : null;
          if (
            previousActivity?.node.activity ===
              ProposalActivityAction.APPROVAL_WITHDRAWN_ON_PUBLISH &&
            previousActivity?.node.target?.__typename === 'ProposalReview'
          ) {
            return <React.Fragment key={activityNode.id} />;
          }

          const previousLaunch =
            activityNode.target?.__typename === 'ProposalRevision'
              ? (activityNode.target.previousRevision?.launch ??
                firstLaunchHistoryItem)
              : firstLaunchHistoryItem;

          const previousReview = _.find(
            activities.slice(index + 1),
            (
              activity,
            ): activity is typeof activity & {
              node: (typeof activity)['node'] & {
                target: (typeof activity)['node']['target'] & {
                  __typename: 'ProposalReview';
                };
              };
            } =>
              activity.node.target?.__typename === 'ProposalReview' &&
              activity.node.createdBy?.id === activityNode.createdBy?.id,
          )?.node.target;

          return (
            <ActivityNode
              canQueryLaunches={canQueryLaunches}
              sourceGraphVariant={sourceGraphVariant}
              usersForMentions={usersForMentions}
              graphRef={graphRef}
              key={activityNode.id}
              proposal={proposal}
              node={activityNode}
              previousReview={previousReview}
              previousLaunch={previousLaunch}
              className="relative w-full p-0"
            />
          );
        })}
    </div>
  );
};

const ActivityNode = ({
  node,
  proposal,
  graphRef,
  className,
  previousLaunch,
  previousReview,
  usersForMentions,
  canQueryLaunches,
  sourceGraphVariant,
}: {
  node: GraphQLTypes.ActivityFeedProposalFragment_activities_edges_node;
  proposal: GraphQLTypes.UseAddCommentProposalFragment;
  graphRef: GraphRef;
  className?: string;
  previousLaunch:
    | GraphQLTypes.ActivityFeedFirstLaunchGraphVariantFragment_launchHistory
    | undefined;
  previousReview:
    | GraphQLTypes.ActivityFeedProposalFragment_activities_edges_node_target_ProposalReview
    | undefined;
  usersForMentions: ReturnType<typeof useGetUsersForMentions>;
  canQueryLaunches: boolean;
  sourceGraphVariant: string | undefined;
}) => {
  const location = useLocation();

  switch (node.activity) {
    case ProposalActivityAction.RETURN_TO_DRAFT_PROPOSAL:
    case ProposalActivityAction.OPEN_PROPOSAL:
    case ProposalActivityAction.CLOSE_PROPOSAL:
      return (
        <div className="mt-3 flex items-start">
          <div className="z-10 mr-4 mt-2 flex h-10 min-w-10 items-center justify-center rounded-full border border-primary bg-primary">
            <IconSync className="text-icon-primary" />
          </div>
          <Card className={className}>
            <CardHeader>
              <Group>
                <ActivityAvatar createdBy={node.createdBy} />
                <CardDescription>
                  <Text as="span" variant="bodySb">
                    {node.createdBy?.__typename === 'User'
                      ? node.createdBy.fullName
                      : 'Unknown'}
                  </Text>{' '}
                  changed the status of this proposal to{' '}
                  <Text as="span" variant="bodySb">
                    {node.activity ===
                    ProposalActivityAction.RETURN_TO_DRAFT_PROPOSAL
                      ? 'draft'
                      : node.activity === ProposalActivityAction.CLOSE_PROPOSAL
                        ? 'closed'
                        : node.activity === ProposalActivityAction.OPEN_PROPOSAL
                          ? 'open for feedback'
                          : assertUnreachable(node.activity)}
                  </Text>
                </CardDescription>
              </Group>
              <CardHeaderControl>
                <RelativeTimestamp
                  value={node.createdAt}
                  className="align-middle text-secondary max-md:hidden"
                />
                <LocalTimestamp
                  value={node.createdAt}
                  className="rounded p-2 align-middle hover:bg-primary md:hidden"
                >
                  <IconTime className="block text-icon-primary" />
                </LocalTimestamp>
              </CardHeaderControl>
            </CardHeader>
          </Card>
        </div>
      );
    case ProposalActivityAction.MET_MIN_APPROVALS_PROPOSAL:
      return (
        <div className="mt-3 flex items-start">
          <div className="z-10 mr-4 mt-2 flex h-10 min-w-10 items-center justify-center rounded-full border border-success bg-success">
            <IconSuccess className="text-icon-success" />
          </div>
          <Card className={className}>
            <CardHeader>
              <CardDescription>
                This proposal has been fully approved
              </CardDescription>
              <CardHeaderControl>
                <RelativeTimestamp
                  value={node.createdAt}
                  className="align-middle text-secondary max-md:hidden"
                />
                <LocalTimestamp
                  value={node.createdAt}
                  className="rounded p-2 hover:bg-primary md:hidden"
                >
                  <IconTime className="block text-icon-primary" />
                </LocalTimestamp>
              </CardHeaderControl>
            </CardHeader>
          </Card>
        </div>
      );
    case ProposalActivityAction.APPROVAL_WITHDRAWN:
      return (
        <div className="mt-3 flex items-start">
          <div className="z-10 mr-4 mt-2 flex h-10 min-w-10 items-center justify-center rounded-full border border-warning bg-warning">
            <IconWarning className="text-icon-warning" />
          </div>
          <Card className={className}>
            <CardHeader>
              <CardDescription>
                This proposal no longer meets approval requirements
              </CardDescription>
              <CardHeaderControl>
                <RelativeTimestamp
                  value={node.createdAt}
                  className="text-secondary max-md:hidden"
                />
                <LocalTimestamp
                  value={node.createdAt}
                  className="rounded p-2 align-middle hover:bg-primary md:hidden"
                >
                  <IconTime className="block text-icon-primary" />
                </LocalTimestamp>
              </CardHeaderControl>
            </CardHeader>
          </Card>
        </div>
      );
    case ProposalActivityAction.REVIEW_ADDED:
      return node.target?.__typename === 'ProposalReview' ? (
        <div className="mt-3 flex items-start">
          <div className="z-10 mr-4 mt-2 flex h-10 min-w-10 items-center justify-center rounded-full border border-primary bg-primary">
            {node.target.decision === ReviewDecision.APPROVED ? (
              <IconSuccess className="text-icon-success" />
            ) : previousReview?.decision === ReviewDecision.APPROVED ? (
              <IconWarning className="text-icon-warning" />
            ) : (
              <IconComment className="text-icon-primary" />
            )}
          </div>
          <Card
            isDivided={!!node.target.comment?.message}
            className={className}
          >
            <CardHeader>
              <Group>
                <ActivityAvatar createdBy={node.createdBy} />
                <CardDescription>
                  <Text as="span" variant="bodySb" className="text-neutral">
                    {node.createdBy?.__typename === 'User'
                      ? node.createdBy.fullName
                      : 'Unknown'}
                  </Text>
                  {node.target.decision === ReviewDecision.APPROVED
                    ? ' has approved this proposal'
                    : previousReview?.decision === ReviewDecision.APPROVED
                      ? ' removed their approval'
                      : ' has reviewed this proposal'}
                </CardDescription>
              </Group>
              <CardHeaderControl>
                <RelativeTimestamp
                  value={node.createdAt}
                  className="text-secondary max-md:hidden"
                />
                <LocalTimestamp
                  value={node.createdAt}
                  className="rounded p-2 hover:bg-primary md:hidden"
                >
                  <IconTime className="block text-icon-primary" />
                </LocalTimestamp>
              </CardHeaderControl>
            </CardHeader>
            {node.target.comment?.message && (
              <CardBody className="text-secondary">
                <SafeCommentMarkdown
                  comment={node.target.comment.message}
                  className="text-secondary"
                  usersForMentions={usersForMentions}
                />
              </CardBody>
            )}
          </Card>
        </div>
      ) : null;
    case ProposalActivityAction.COMMENT_ADDED:
      return node.target?.__typename === 'ParentChangeProposalComment' ||
        node.target?.__typename === 'ParentGeneralProposalComment' ? (
        <div className="mt-3 flex items-start">
          <div className="z-10 mr-4 mt-2 flex h-10 min-w-10 items-center justify-center rounded-full border border-primary bg-primary">
            <IconComment className="text-icon-primary" />
          </div>
          <ActivityFeedCommentThread
            graphRef={graphRef}
            proposal={proposal}
            comment={node.target}
            className={className}
            usersForMentions={usersForMentions}
          />
        </div>
      ) : null;
    case ProposalActivityAction.DELETE_SUBGRAPH:
    case ProposalActivityAction.PUBLISH_SUBGRAPHS: {
      if (!previousLaunch) {
        return (
          <div className="mt-3 flex items-start">
            <div className="z-10 mr-4 mt-2 flex h-10 min-w-10 items-center justify-center rounded-full border border-primary bg-primary" />
            <Card
              isDivided={Boolean(
                node.target?.__typename === 'ProposalRevision' &&
                  node.target.summary,
              )}
              className={className}
            >
              <CardHeader>
                <Group>
                  <ActivityAvatar createdBy={node.createdBy} />
                  <CardDescription>
                    <Text as="span" variant="bodySb">
                      {node.createdBy?.__typename === 'User'
                        ? node.createdBy.fullName
                        : node.createdBy?.__typename === 'InternalIdentity' &&
                            node.createdBy.id === 'proposals'
                          ? 'Proposals System'
                          : 'Unknown'}
                    </Text>{' '}
                    made changes to the <IconServices />{' '}
                    <Text as="span" variant="bodySb" className="text-neutral">
                      API Schema
                    </Text>
                  </CardDescription>
                </Group>
                <CardHeaderControl>
                  <RelativeTimestamp
                    value={node.createdAt}
                    className="align-middle text-secondary max-md:hidden"
                  />
                  <LocalTimestamp
                    value={node.createdAt}
                    className="rounded p-2 align-middle hover:bg-primary md:hidden"
                  >
                    <IconTime className="block text-icon-primary" />
                  </LocalTimestamp>
                </CardHeaderControl>
              </CardHeader>
              {node.target?.__typename === 'ProposalRevision' &&
                node.target.summary && (
                  <CardBody className="text-secondary">
                    <SafeCommentMarkdown
                      comment={node.target.summary}
                      className="text-secondary"
                      usersForMentions={usersForMentions}
                    />
                  </CardBody>
                )}
            </Card>
          </div>
        );
      }

      if (
        node.target?.__typename !== 'ProposalRevision' ||
        node.target.launch?.id === previousLaunch.id
      )
        return null;

      return (
        <ActivityFeedRevision
          proposalStatus={proposal.status}
          canQueryLaunches={canQueryLaunches}
          sourceGraphVariant={sourceGraphVariant}
          graphRef={graphRef}
          target={node.target}
          node={node}
          previousLaunch={previousLaunch}
          proposal={proposal}
          className={className}
        />
      );
    }
    case ProposalActivityAction.FULLY_IMPLEMENTED_PROPOSAL_ORIGIN:
    case ProposalActivityAction.FULLY_IMPLEMENTED_VARIANT_ORIGIN:
    case ProposalActivityAction.PARTIALLY_IMPLEMENTED_PROPOSAL_ORIGIN:
    case ProposalActivityAction.PARTIALLY_IMPLEMENTED_VARIANT_ORIGIN: {
      const revisionId =
        node.target?.__typename ===
          'ProposalFullImplementationProposalOrigin' ||
        node.target?.__typename ===
          'ProposalPartialImplementationProposalOrigin'
          ? node.target.revision?.id
          : undefined;

      const launchId =
        node.target?.__typename === 'ProposalFullImplementationVariantOrigin' ||
        node.target?.__typename === 'ProposalPartialImplementationVariantOrigin'
          ? node.target.launch?.id
          : undefined;

      const targetVariant =
        node.target?.__typename ===
          'ProposalFullImplementationProposalOrigin' ||
        node.target?.__typename === 'ProposalFullImplementationVariantOrigin' ||
        node.target?.__typename ===
          'ProposalPartialImplementationProposalOrigin' ||
        node.target?.__typename === 'ProposalPartialImplementationVariantOrigin'
          ? node.target.variant.name
          : '';

      const isFullyImplemented =
        node.activity ===
          ProposalActivityAction.FULLY_IMPLEMENTED_PROPOSAL_ORIGIN ||
        node.activity ===
          ProposalActivityAction.FULLY_IMPLEMENTED_VARIANT_ORIGIN;
      return (
        <div className="mt-3 flex items-start">
          <div
            className={classnames(
              'z-10 mr-4 mt-2 flex h-10 min-w-10 items-center justify-center rounded-full border ',
              isFullyImplemented
                ? 'border-beta bg-beta'
                : 'border-primary bg-primary',
            )}
          >
            <IconRocket
              className={classnames(
                isFullyImplemented ? 'text-icon-primary' : 'text-icon-beta',
              )}
            />
          </div>
          <Card className={className}>
            <CardHeader>
              <CardDescription>
                This proposal has been{' '}
                <Text as="span" variant="bodySb">
                  {isFullyImplemented ? 'fully' : 'partially'} implemented
                </Text>{' '}
                on{' '}
                <Link
                  to={variantHomepageRouteConfig.locationFrom({
                    location,
                    fromRouteConfig: proposalHomepageRouteConfig,
                    patch: {
                      graphVariant: targetVariant,
                    },
                  })}
                >
                  {targetVariant}
                </Link>
                {revisionId ? (
                  <>
                    {' '}
                    with revision{' '}
                    <ReactRouterLink
                      to={proposalChangesRouteConfig.locationFrom({
                        location,
                        fromRouteConfig: proposalHomepageRouteConfig,
                        patch: {
                          selectedRevision: revisionId,
                        },
                      })}
                    >
                      <Code title={revisionId}>
                        {revisionId.substring(0, 8)}
                      </Code>
                    </ReactRouterLink>
                  </>
                ) : launchId ? (
                  <>
                    {' '}
                    with launch{' '}
                    <ReactRouterLink
                      to={launchesRouteConfig.locationFrom({
                        location,
                        fromRouteConfig: proposalHomepageRouteConfig,
                        patch: {
                          graphVariant: targetVariant,
                          launchId,
                        },
                      })}
                    >
                      <Code title={launchId}>{launchId.substring(0, 8)}</Code>
                    </ReactRouterLink>
                  </>
                ) : (
                  '.'
                )}
              </CardDescription>
              <CardHeaderControl>
                <RelativeTimestamp
                  value={node.createdAt}
                  className="text-secondary max-md:hidden"
                />
                <LocalTimestamp
                  value={node.createdAt}
                  className="rounded p-2 hover:bg-primary md:hidden"
                >
                  <IconTime className="block text-icon-primary" />
                </LocalTimestamp>
              </CardHeaderControl>
            </CardHeader>
          </Card>
        </div>
      );
    }
    case ProposalActivityAction.APPROVAL_WITHDRAWN_ON_PUBLISH: {
      return (
        <div className="mt-3 flex items-start">
          <div
            className={classnames(
              'z-10 mr-4 mt-2 flex h-10 min-w-10 items-center justify-center rounded-full border',
              node.target?.__typename === 'Proposal'
                ? 'border-primary bg-primary'
                : 'border-warning bg-warning',
            )}
          >
            {node.target?.__typename === 'ProposalReview' ? (
              <IconWarning className="text-icon-warning" />
            ) : (
              <IconSync className="text-icon-primary" />
            )}
          </div>
          <Card className={className}>
            <CardHeader>
              {node.target?.__typename === 'ProposalReview' ? (
                <CardDescription>
                  This proposal has been updated after one or more approvals
                  were sent, causing previous approvals to be withdrawn and
                  reviewers to be re-requested. You can{' '}
                  {sourceGraphVariant && (
                    <>
                      <Link
                        to={routes.graphSettingsRouteConfig.location({
                          graphId: graphRef.graphId,
                          graphVariant: sourceGraphVariant,
                          graphVisibilityType: 'graph',
                          tab: 'proposals',
                        })}
                      >
                        configure this preference in graph settings
                      </Link>
                      .
                    </>
                  )}
                </CardDescription>
              ) : node.target?.__typename === 'Proposal' ? (
                <CardDescription>
                  Due to new updates, the status of this proposal has been
                  changed to{' '}
                  <Text as="span" variant="bodySb">
                    open for feedback
                  </Text>
                  .
                </CardDescription>
              ) : (
                <CardDescription>Unknown</CardDescription>
              )}
              <CardHeaderControl>
                <RelativeTimestamp
                  value={node.createdAt}
                  className="text-secondary max-md:hidden"
                />
                <LocalTimestamp
                  value={node.createdAt}
                  className="rounded p-2 hover:bg-secondary md:hidden"
                >
                  <IconTime className="block text-icon-primary" />
                </LocalTimestamp>
              </CardHeaderControl>
            </CardHeader>
          </Card>
        </div>
      );
    }
    case null:
      return null;
    default:
      return assertUnreachableOrReturnDefault(
        node.activity,
        <Card className={className}>
          <CardBody>Unknown action</CardBody>
        </Card>,
      );
  }
};

const ActivityAvatar = ({
  createdBy,
}: {
  createdBy:
    | GraphQLTypes.ActivityFeedProposalFragment_activities_edges_node['createdBy']
    | null;
}) => {
  return createdBy?.__typename === 'User' ? (
    <Avatar
      size="sm"
      name={createdBy.fullName}
      src={createdBy.avatarUrl ?? undefined}
    />
  ) : (
    <Avatar size="sm" />
  );
};
